import type { Option } from '@branchjs/neo-components';
import * as queryString from 'query-string';
import { createSelector } from 'reselect';

import { EMPTY_ARRAY } from 'client/js/redux/constants/ReduxAsyncActions.constants';
import type { RootState } from 'client/js/redux/types/redux.types';
import CustomPartnerService from '../../../../../shared/CustomPartner.service';
import { CUSTOM_PARTNER_NUMBER_UNLIMITED } from '../../../../../shared/constants';
import { RESTRICT_SNAP_REPORTS } from '../../../../../shared/constants/flipperFlags';
import type { CustomPartnerData, CustomPartnerLimitResponse } from '../../../../../shared/types/customPartnerLimits.types';
import RokuDefaults from '../../../main/components/PartnerManager/Roku/RokuDefaults';
import { LIMITED_DATA_USE } from '../../../main/components/PartnerManager/constants';
import { isUserOnAppView } from '../../../main/selectors/OrgSelectors';
import type {
  AdOntology,
  AdPartner,
  AdPartnerConnectionData,
  AdPartnerCredentials,
  AdPartnerState,
  AdPartnerWithOnlyDataForSKAN,
  ExtraData,
  FacebookData,
  RokuSettings,
  SnapAppIds,
  SnapData,
  TikTokAccount,
} from '../../../main/types/adPartnerTypes';
import { isFeatureEnabled } from '../FlipperUpdates/Flipper.selectors';
import { INIT_STATE } from './AdPartner.constants';

export const ENGAGEMENT_BUILDER_PARTNER_BLACKLIST = ['a_facebook', 'a_pinterest', 'a_reddit', 'a_roku'];
export function isAdPartnerVisible(partner: AdPartner | AdPartnerWithOnlyDataForSKAN) {
  return (
    partner.status === 'active' ||
    // case: custom ad partner. Always show
    CustomPartnerService.isCustomPartner(partner) ||
    // case: tune ad network blueprint. Show if a configuration exists (entry in ad_network).
    (partner.status === 'tune' && !partner.neverConfigured)
  );
}

export function isAdPartnerPartOfAnySKAdNetwork(partner: AdPartner | AdPartnerWithOnlyDataForSKAN) {
  return Boolean(partner.sk_ad_network_id);
}

export function isAppLevelAdPartner(partner: AdPartner | AdPartnerWithOnlyDataForSKAN) {
  return !Boolean(partner.organization_id);
}

export function getState(state: RootState): AdPartnerState {
  return (state && state.ad_partner) || INIT_STATE;
}

export const getSelectedPartner: (state: RootState) => AdPartner | null = createSelector(
  getState,
  (state) => (state && state.selectedPartner) || null
);

export const getSelectedPartnerExtraData: (state: RootState) => ExtraData | null | undefined = createSelector(
  getSelectedPartner,
  (state) => {
    return (state && state.extra_data) || null;
  }
);

export const getSelectedPartnerExtraDataSnapAppIds: (state: RootState) => SnapAppIds | null = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.snap_ad_app_ids) || null;
  }
);

export const getSelectedPartnerExtraDataSnapData: (state: RootState) => SnapData | null = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.snap_data) || null;
  }
);

export const getSelectedPartnerSnapEnableAdvancedConversion: (state: RootState) => boolean = createSelector(
  getSelectedPartnerExtraDataSnapData,
  (state) => {
    return (state && state.ios_user_data_send_enabled) || false;
  }
);

export const getSelectedPartnerExtraDataTikTokAccounts: (state: RootState) => TikTokAccount[] | null = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.tiktok_accounts) || null;
  }
);

export const getPartners: (state: RootState) => AdPartner[] = createSelector(
  getState,
  (state) => state?.partners || EMPTY_ARRAY
);

export const selectAdPartnerLookup: (state: RootState) => Record<string, AdPartner> = createSelector(
  getPartners,
  (adPartners) =>
    adPartners.reduce(
      (acc, partner) => ({
        ...acc,
        [partner.machine_name]: partner,
      }),
      {}
    )
);

export const selectAdPartnerNameLookup: (state: RootState) => Record<string, string> = createSelector(
  getPartners,
  (adPartners) =>
    adPartners.reduce(
      (acc, partner) => ({
        ...acc,
        [partner.machine_name]: partner.company_name || partner.publisher_name || partner.machine_name,
      }),
      {}
    )
);

export const selectEnabledAdPartnerOptions: (state: RootState) => Option[] = createSelector(getPartners, (adPartners) =>
  adPartners
    .filter((partner) => partner.enabled)
    .map((partner) => ({
      label: partner.company_name || partner.publisher_name || partner.machine_name,
      value: partner.machine_name,
    }))
);

export const selectVisibleAdPartners: (state: RootState) => AdPartner[] = createSelector(getPartners, (partners) =>
  partners.filter(isAdPartnerVisible)
);

export const selectEngagementBuilderEnabledAdPartners: (state: RootState) => AdPartner[] = createSelector(
  selectVisibleAdPartners,
  (state) => isFeatureEnabled(state, RESTRICT_SNAP_REPORTS),
  (partners, snapReportsRestricted) =>
    partners.filter((adPartner) => {
      const blackListedPartners = [...ENGAGEMENT_BUILDER_PARTNER_BLACKLIST, snapReportsRestricted && 'a_snap'];
      return !blackListedPartners.includes(adPartner.machine_name);
    })
);

export const isLoadingAdPartner: (state: RootState) => boolean = createSelector(
  getState,
  (state) => (state && state.loading) || false
);

export const hasLoadedAdPartners: (state: RootState) => boolean = createSelector(
  getState,
  (state) => (state && state.hasPartnersLoaded) || false
);

export const selectFacebookData: (state: RootState) => FacebookData | null | undefined = createSelector(
  getSelectedPartnerExtraData,
  (extraData) => {
    return extraData && extraData.facebook_data;
  }
);

export const selectFacebookLimitedDataOptions: (state: RootState) => string[] | null | undefined = createSelector(
  selectFacebookData,
  (facebookData) => {
    return facebookData && facebookData.limited_data_options;
  }
);

export const selectFacebookLimitedDataUse: (state: RootState) => boolean = createSelector(
  selectFacebookLimitedDataOptions,
  (facebookLimitedDataOptions) => {
    if (!facebookLimitedDataOptions) {
      // the default value for LIMITED_DATA_USE is true in the backend,
      // and dashboard needs to reflect this default value if no explicit
      // option is set.
      return true;
    }

    return facebookLimitedDataOptions.includes(LIMITED_DATA_USE);
  }
);

export const selectFacebookAEMEnabled: (state: RootState) => boolean = createSelector(selectFacebookData, (facebookData) => {
  return Boolean(facebookData && facebookData.enable_aggregated_event_measurement);
});

export const getSelectedPartnerExtraDataRokuSettings: (state: RootState) => RokuSettings = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.roku_settings) || RokuDefaults.Data;
  }
);

export const selectAppInheritsFromOrg: (state: RootState) => boolean = createSelector(getSelectedPartner, (state) => {
  const { app_id, organization_id } = state || {};
  return !!(app_id && organization_id);
});

export const getCredentialsFromSelectedPartnerOrDefault: (state: RootState) => AdPartnerCredentials = createSelector(
  getSelectedPartner,
  (state: AdPartner | null) => {
    return state?.credentials || {};
  }
);

export const getLoadingState: (state: RootState) => boolean = createSelector(getState, (state) => Boolean(state.loading));

export const selectCustomPartnerLimits: (state: RootState) => CustomPartnerLimitResponse | null = createSelector(
  getState,
  (state) => state.customPartnerLimits
);

export const selectCustomPartnerLimitsLoading: (state: RootState) => boolean = createSelector(
  getState,
  (state) => state.customPartnerLimitsLoading
);

export const selectCustomPartnerLimitsLimit: (state: RootState) => number | undefined = createSelector(
  selectCustomPartnerLimits,
  (customPartnerLimits) => customPartnerLimits?.limit
);

export const selectCustomPartnersBasedOnView: (state: RootState) => CustomPartnerData[] = createSelector(
  selectCustomPartnerLimits,
  selectCustomPartnerLimitsLoading,
  isUserOnAppView,
  (customPartnerLimits, loading, isOnAppView) => {
    if (!customPartnerLimits?.customPartners || loading) return [];

    if (isOnAppView) {
      return customPartnerLimits.customPartners.filter(({ isEnabled, isOrgLevel }) => isEnabled && !isOrgLevel);
    } else {
      return customPartnerLimits.customPartners.filter(({ isEnabled, isOrgLevel }) => isEnabled && isOrgLevel);
    }
  }
);

export const isAddingNewCustomPartnerAllowed: (state: RootState) => boolean = createSelector(
  selectCustomPartnerLimits,
  selectCustomPartnerLimitsLoading,
  isUserOnAppView,
  (customPartnerLimits, loading, isOnAppView) => {
    if (!customPartnerLimits || !customPartnerLimits.customPartners || loading) return false;

    if (customPartnerLimits.limit === CUSTOM_PARTNER_NUMBER_UNLIMITED) {
      return true;
    }

    if (isOnAppView) {
      const appLevelPartners = customPartnerLimits.customPartners.filter(
        ({ isEnabled, isOrgLevel }) => isEnabled && !isOrgLevel
      );
      return appLevelPartners.length < customPartnerLimits.limit;
    } else {
      const orgLevelPartners = customPartnerLimits.customPartners.filter(
        ({ isEnabled, isOrgLevel }) => isEnabled && isOrgLevel
      );
      return orgLevelPartners.length < customPartnerLimits.limit;
    }
  }
);

export const selectAdOntology: (state: RootState) => AdOntology[] | undefined = createSelector(
  getState,
  (state) => state?.ad_ontology
);

export const selectBackUpPartners: (state: RootState) => AdPartner[] = createSelector(
  getState,
  (state) => state.partnersBackup
);

export const selectBackUpPartner: (state: RootState) => AdPartner | undefined | null = createSelector(
  selectBackUpPartners,
  getSelectedPartner,
  (backUpPartners, selectedPartner) => {
    if (!selectedPartner) {
      return null;
    }

    return backUpPartners.find((backUpPartner) => backUpPartner.machine_name === selectedPartner.machine_name);
  }
);

export const selectAdPartnerConnections: (state: RootState) => AdPartnerConnectionData[] = createSelector(
  getState,
  (state) => (state && state.connections) || []
);

export const selectActiveConnection: (state: RootState) => AdPartnerConnectionData | undefined = createSelector(
  selectAdPartnerConnections,
  (connections) => {
    return (connections || []).find(
      (connection) => connection.credential_id === queryString.parse(location.search).connection_id
    );
  }
);

export const selectConnectionsLoading: (state: RootState) => boolean = createSelector(
  getState,
  (state) => state.connectionsLoading
);
